/* global structuredClone */
import type {
  LayoutImageElement,
  LayoutScene,
  LayoutSceneElement,
  LayoutTextElement,
} from './types'

export interface BackendLayoutScene {
  schemaVersion: 1
  templateVersionId: string
  page: { widthMm: 210; heightMm: 297; background: '#FFFFFF' }
  metadata?: Record<string, unknown>
  elements: Array<Record<string, unknown>>
}

const finite = (value: unknown, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback
const clamp = (value: unknown, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, finite(value)))

export function resizeFreeImageWidth(image: LayoutImageElement, widthMm: number): LayoutImageElement {
  if (image.kind !== 'free' || !image.widthMm || !image.heightMm || !Number.isFinite(widthMm) || widthMm <= 0) return image
  const factor = Math.min(widthMm / image.widthMm, (210 - (image.xMm || 0)) / image.widthMm, (297 - (image.yMm || 0)) / image.heightMm)
  return { ...image, widthMm: image.widthMm * factor, heightMm: image.heightMm * factor }
}

export function translateSceneElement(scene: LayoutScene, elementId: string, dxMm: number, dyMm: number): LayoutScene {
  const selected = scene.elements.find(element => element.id === elementId)
  if (!selected || selected.kind === 'frame' || !Number.isFinite(dxMm) || !Number.isFinite(dyMm)) return scene
  let result = scene
  const targets = selected.kind === 'free' && selected.compositionGroupId
    ? scene.elements.filter(element => element.kind === 'free' && element.compositionGroupId === selected.compositionGroupId)
    : [selected]
  for (const element of targets) {
    if (element.kind === 'frame') continue
    const next = element.kind === 'image'
      ? { ...element, transform: { ...element.transform, offsetXMm: element.transform.offsetXMm + dxMm, offsetYMm: element.transform.offsetYMm + dyMm } }
      : { ...element, xMm: (element.xMm || 0) + dxMm, yMm: (element.yMm || 0) + dyMm }
    result = updateSceneElement(result, next)
  }
  return result
}

export function replaceSceneImage(scene: LayoutScene, elementId: string, assetId: string, sourceUrl?: string | null): LayoutScene {
  const selected = scene.elements.find(element => element.id === elementId)
  if (!assetId || !selected || (selected.kind !== 'image' && selected.kind !== 'free')) return scene
  return updateSceneElement(scene, { ...selected, assetId, sourceUrl: sourceUrl || undefined })
}

/**
 * Подогнать рамку свободной картинки под пропорции её файла: вписать в прежнюю рамку по
 * центру. Замена картинки (лента серии, выбор из библиотеки) оставляла рамку прежнего
 * элемента, а картинка вписывается в неё («contain») — у торта на месте широкой плашки
 * рамка выходила вдвое шире рисунка, и её растягивание раздувало пустоту (живая правка
 * топперов 01.10.2026). Применяется, только пока на элементе всё ещё этот файл.
 */
export function fitFreeImageFrame(scene: LayoutScene, elementId: string, assetId: string, widthPx: number, heightPx: number): LayoutScene {
  const element = scene.elements.find(candidate => candidate.id === elementId)
  if (!element || element.kind !== 'free' || element.assetId !== assetId) return scene
  if (!(widthPx > 0) || !(heightPx > 0)) return scene
  const { xMm = 0, yMm = 0, widthMm = 0, heightMm = 0 } = element
  if (!(widthMm > 0) || !(heightMm > 0)) return scene
  const aspect = widthPx / heightPx
  const fitWidth = Math.min(widthMm, heightMm * aspect)
  const fitHeight = fitWidth / aspect
  if (Math.abs(fitWidth - widthMm) < 0.1 && Math.abs(fitHeight - heightMm) < 0.1) return scene
  return updateSceneElement(scene, {
    ...element,
    xMm: xMm + (widthMm - fitWidth) / 2,
    yMm: yMm + (heightMm - fitHeight) / 2,
    widthMm: fitWidth,
    heightMm: fitHeight,
  })
}

function boundedElement(element: LayoutSceneElement): LayoutSceneElement {
  if (element.kind === 'frame') return element
  if (element.kind === 'text') {
    const xMm = clamp(element.xMm, 0, 210)
    const yMm = clamp(element.yMm, 0, 297)
    return {
      ...element,
      xMm,
      yMm,
      widthMm: element.widthMm == null ? undefined : clamp(element.widthMm, 0, 210 - xMm),
      heightMm: element.heightMm == null ? undefined : clamp(element.heightMm, 0, 297 - yMm),
      fontSizePt: clamp(element.fontSizePt, 6, 300),
      ...(element.strokeWidthPt == null
        ? {}
        : { strokeWidthPt: clamp(element.strokeWidthPt, 0, 20) }),
      ...(element.backgroundPaddingMm == null
        ? {}
        : { backgroundPaddingMm: clamp(element.backgroundPaddingMm, 0, 50) }),
    }
  }
  const xMm = element.xMm == null ? undefined : clamp(element.xMm, 0, 210)
  const yMm = element.yMm == null ? undefined : clamp(element.yMm, 0, 297)
  return {
    ...element,
    xMm,
    yMm,
    widthMm: element.widthMm == null ? undefined : clamp(element.widthMm, 0, 210 - (xMm || 0)),
    heightMm: element.heightMm == null ? undefined : clamp(element.heightMm, 0, 297 - (yMm || 0)),
    transform: {
      ...element.transform,
      scale: clamp(element.transform?.scale ?? 1, 0.1, 10),
      offsetXMm: clamp(element.transform?.offsetXMm ?? 0, -210, 210),
      offsetYMm: clamp(element.transform?.offsetYMm ?? 0, -297, 297),
      rotationDeg: finite(element.transform?.rotationDeg, 0),
    },
  }
}

export function normalizeLayoutScene(
  raw: unknown,
  fallbackTemplateVersionId = '',
): LayoutScene | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const value = raw as Record<string, any>
  if (!Array.isArray(value.elements)) return null
  const elements = value.elements.flatMap((entry: Record<string, any>): LayoutSceneElement[] => {
    const bounds =
      entry.bounds && typeof entry.bounds === 'object' && !Array.isArray(entry.bounds)
        ? entry.bounds
        : entry
    const kind =
      entry.kind === 'shape' || (entry.kind === 'image' && entry.bounds && !entry.slotId)
        ? 'free'
        : entry.kind
    if (kind === 'frame') {
      return [
        {
          id: String(entry.id),
          kind: 'frame',
          slotId: String(entry.slotId ?? entry.slot_id ?? entry.id),
          fixed: true,
          xMm: finite(bounds.xMm),
          yMm: finite(bounds.yMm),
          widthMm: finite(bounds.widthMm),
          heightMm: finite(bounds.heightMm),
          shape: ['circle', 'square', 'free'].includes(entry.shape) ? entry.shape : 'free',
        },
      ]
    }
    if (kind === 'text') {
      return [
        boundedElement({
          id: String(entry.id),
          kind: 'text',
          slotId: entry.slotId ?? entry.slot_id,
          value: String(entry.value ?? ''),
          ...(entry.compositionGroupId ? { compositionGroupId: String(entry.compositionGroupId) } : {}),
          ...(entry.layerRole ? { layerRole: entry.layerRole } : {}),
          fontId: String(entry.fontId ?? entry.font_id ?? ''),
          fontSizePt: finite(entry.fontSizePt ?? entry.font_size_pt, 12),
          fill: String(entry.fill ?? '#000000'),
          ...(entry.strokeColor == null && entry.stroke_color == null
            ? {}
            : { strokeColor: String(entry.strokeColor ?? entry.stroke_color) }),
          ...(entry.strokeWidthPt == null && entry.stroke_width_pt == null
            ? {}
            : { strokeWidthPt: finite(entry.strokeWidthPt ?? entry.stroke_width_pt, 0) }),
          ...(entry.backgroundColor == null && entry.background_color == null
            ? {}
            : { backgroundColor: String(entry.backgroundColor ?? entry.background_color) }),
          ...(entry.backgroundPaddingMm == null && entry.background_padding_mm == null
            ? {}
            : {
                backgroundPaddingMm: finite(
                  entry.backgroundPaddingMm ?? entry.background_padding_mm,
                  0,
                ),
              }),
          align: ['left', 'center', 'right'].includes(entry.align) ? entry.align : 'left',
          xMm: finite(bounds.xMm),
          yMm: finite(bounds.yMm),
          widthMm: finite(bounds.widthMm, 1),
          heightMm: finite(bounds.heightMm, 1),
        } as LayoutTextElement),
      ]
    }
    if (kind === 'image' || kind === 'free') {
      return [
        boundedElement({
          ...entry,
          id: String(entry.id),
          kind,
          assetId: String(entry.assetId ?? entry.asset_id ?? ''),
          sourceUrl: entry.sourceUrl ?? entry.source_url ?? null,
          xMm: kind === 'free' ? finite(bounds.xMm) : undefined,
          yMm: kind === 'free' ? finite(bounds.yMm) : undefined,
          widthMm: kind === 'free' ? finite(bounds.widthMm, 1) : undefined,
          heightMm: kind === 'free' ? finite(bounds.heightMm, 1) : undefined,
          transform: entry.transform || {},
        } as LayoutImageElement),
      ]
    }
    return []
  })
  return {
    schemaVersion: 1,
    templateVersionId: String(
      value.templateVersionId ?? value.template_version_id ?? fallbackTemplateVersionId,
    ),
    page: { widthMm: 210, heightMm: 297, background: '#FFFFFF' },
    metadata:
      value.metadata && typeof value.metadata === 'object' && !Array.isArray(value.metadata)
        ? { ...value.metadata }
        : {},
    elements,
  }
}

/** Removes read-only template geometry and serializes the editable scene accepted by design-service. */
export function serializeLayoutScene(scene: LayoutScene): BackendLayoutScene {
  return {
    schemaVersion: 1,
    templateVersionId: scene.templateVersionId,
    page: { widthMm: 210, heightMm: 297, background: '#FFFFFF' },
    ...(scene.metadata ? { metadata: { ...scene.metadata } } : {}),
    elements: scene.elements
      .filter((element) => element.kind !== 'frame')
      .map((element) => {
        const bounded = boundedElement(element)
        if (bounded.kind === 'frame')
          throw new Error('Фиксированная геометрия шаблона не сериализуется')
        if (bounded.kind === 'text') {
          const { xMm, yMm, widthMm, heightMm, slotId, ...text } = bounded
          return {
            ...text,
            ...(slotId ? { slotId } : {}),
            bounds: { xMm, yMm, widthMm: widthMm ?? 1, heightMm: heightMm ?? 1 },
          }
        }
        const { xMm, yMm, widthMm, heightMm, sourceUrl: _sourceUrl, ...image } = bounded
        if (bounded.kind === 'free' && bounded.assetId) {
          return {
            ...image,
            kind: 'image',
            bounds: {
              xMm: xMm ?? 0,
              yMm: yMm ?? 0,
              widthMm: widthMm ?? 1,
              heightMm: heightMm ?? 1,
            },
          }
        }
        if (bounded.kind === 'free') return { ...image, kind: 'shape' }
        const { bounds: _bounds, ...slottedImage } = image as typeof image & {
          bounds?: unknown
        }
        return { ...slottedImage, kind: 'image' }
      }),
  }
}

export function cloneLayoutScene(scene: LayoutScene): LayoutScene {
  return typeof structuredClone === 'function'
    ? structuredClone(scene)
    : JSON.parse(JSON.stringify(scene))
}

/** Template frames are immutable; only their content and other editable elements may change. */
export function updateSceneElement(scene: LayoutScene, next: LayoutSceneElement): LayoutScene {
  if (next.kind === 'frame') return scene
  const current = scene.elements.find((element) => element.id === next.id)
  if (!current || current.kind === 'frame') return scene
  const copy = cloneLayoutScene(scene)
  copy.elements = copy.elements.map((element) => (element.id === next.id ? next : element))
  return copy
}

export function sceneChanged(
  baseline: LayoutScene | null | undefined,
  current: LayoutScene | null | undefined,
): boolean {
  return Boolean(baseline && current && JSON.stringify(baseline) !== JSON.stringify(current))
}
